﻿import '../../js/utilities/dropzone.js';
import Modal from "./modal";
import Confirmation from "./confirmation";
import RequestVerificationToken from "../utilities/requestverificationtoken";
import $ from 'jquery';

declare global {
  interface JQuery {
    dropzone(IDropzoneOptions?: any): JQuery;
    thumbnailupload: (option) => any;
  }

  interface HTMLElement {
    dropzone: Dropzone;
  }

  interface File {
      previewElement: HTMLElement;
      size: number;
  }
}

export interface Dropzone {
  clickableElements: HTMLElement[];
  files: File[];

  optionCallbacks: any;

  addClickableElement(clickableElement: HTMLElement, dropzone: Dropzone);
  addElementAsDragAndDropArea(element: HTMLElement, dropzone: Dropzone, dragHoverClass?: string);
  addFile(file: File);
  initExistingFile(file: File);
  emit(action: string, file: File, errorMessage: string, e: any);
  getUploadingFiles(): File[];
  getQueuedFiles(): File[];
  removeFile(file: File);
  updatePreviewContainer(previewContainer: HTMLElement, previewContainerPrepend: HTMLElement);
}


export class ThumbnailUpload {
  static get wireup(): string { return '[data-thumbnailupload-id]'; }

  private static get thumbnailId(): string { return 'tu-thumbnail-id'; }
  private static get uploadSuccess(): string { return 'tu-success'; }
  private static get uploadError(): string { return 'tu-error'; }
  private static get thumbnailRemove(): string { return 'tu-remove'; }
  private static get dropzonePreserver(): string { return 'thumbnailUploadPreserver'; }
  private static get prependAttribute(): string { return 'tu-prepend'; }
  private static get previewTemplate(): string { return 'tu-preview-template'; }
  private static get tuUploading(): string { return 'tu-uploading'; }
  private static get tuEmpty(): string { return 'tu-empty'; }

  public DEFAULTS: IThumbnailUploadOptions = {
    $dropzone: null,
    $previewContainer: null,
    uniqueId: null
  }

  private $element: JQuery;
  private options: IThumbnailUploadOptions;
  private dropzone: Dropzone;

  public constructor(options: IThumbnailUploadOptions) {
    this.options = $.extend({}, this.DEFAULTS, options);

    if (!this.options.$dropzone || this.options.$dropzone.length <= 0)
      throw new Error('$dropzone cannot be null');
    if (!this.options.$previewContainer || this.options.$previewContainer.length <= 0)
      throw new Error('$previewContainer cannot be null');
    if (!this.options.uniqueId)
      throw new Error('uniqueId cannot be null');

    this.$element = this.options.$dropzone;

    let uploadPreserverId = 'thumbnailUploadPreserver' + this.options.uniqueId;
    let $uploadPreserver = $('#' + uploadPreserverId);
    if ($uploadPreserver.length <= 0)
      this.createUploadPreserver(uploadPreserverId);
    else
      this.updateDropzone($uploadPreserver);
  }

  public addElementAsDragAndDropArea(element: HTMLElement) {
    this.dropzone.addElementAsDragAndDropArea(element, this.dropzone, 'tu-drag-hover');
  }

  public initFile(file: File) {
    this.dropzone.initExistingFile(file);
  }

  public addFile(file: File) {
    this.dropzone.addFile(file);
  }

  public removeFile(file: File) {
      if (!file)
        return;

    this.dropzone.removeFile(file);
  }

    public getUploadingFiles(): File[] {
      return this.dropzone.getUploadingFiles();
    }

    public getAllFiles(): File[] {
      return this.dropzone.files;
    }

  private createUploadPreserver(id: string): void {
    let $uploadPreserver = $('<div hidden id="' + id + '"></div>');
    $(document.body).append($uploadPreserver);
    $uploadPreserver.dropzone(this.getDefaultOptions());
    this.dropzone = $uploadPreserver[0].dropzone;
  }

  private getDefaultOptions(): any {
    let self = this;

    return {
      accept: this.options.accept ? (file, done) => this.dropzone.optionCallbacks.accept(file, done) : (file, done) => done(),
      acceptedFiles: self.$element.data('tu-file-types'),
      clickable: false,
      clickableElements: [self.$element[0]],
      confirm: ThumbnailUpload.confirm,
      dictDefaultMessage: '',
      hiddenInputContainer: '#' + ThumbnailUpload.dropzonePreserver + self.options.uniqueId,
      maxFiles: self.$element.data('tu-max-files') || null,
      maxFilesize: self.$element.data('tu-max-file-size') || 256,
      maxThumbnailFilesize: self.$element.data('tu-max-thumbnail-filesize') || 10,
      parallelUploads: self.$element.data('tu-parallel-uploads') || 2,
      previewsContainer: self.options.$previewContainer[0],
      previewsContainerPrepend: self.options.$previewContainer.find(ThumbnailUpload.prependAttribute.asDataSelector())[0],
      previewTemplate: self.getPreviewTemplate() || self.getDefaultPreviewTemplate(),
      removedfile: function () { },
      renameFilename: (name: string) => encodeURIComponent(name),
      thumbnailHeight: 150,
      thumbnailSquare: true,
      thumbnailWidth: 150,
      uploadMultiple: self.$element.data('tu-upload-multiple') ? true : null,
      url: self.$element.data('tu-url'),

      init: function () {
        this.on('addedfile', function (file: File) {
          let dropzone = this;
          let $thumbnail = $(file.previewElement);
          $thumbnail.find('[data-tu-name]').attr('title', file.name).attr('value', file.name).text(file.name);
          $thumbnail.find('[data-tu-remove]').on('click.rustig', () => dropzone.removeFile(file));
        });
        let removeDragHoverClass = function () {
          let removeClass = (value: HTMLElement) => value.classList.remove('tu-drag-hover');
          self.dropzone.clickableElements.forEach(removeClass);
        };
        this.on('drop', removeDragHoverClass);
        this.on('dragend', removeDragHoverClass);
        this.on('dragleave', removeDragHoverClass);
        let addDragHoverClass = function () {
          let addClass = (value: HTMLElement) => value.classList.add('tu-drag-hover');
          self.dropzone.clickableElements.forEach(addClass);
        };
        this.on('dragenter', addDragHoverClass);
        this.on('dragover', addDragHoverClass);
        this.on('thumbnail', function (file: File, dataUrl: string) {
          let $thumbnail = $(file.previewElement);
          $thumbnail.find('[data-tu-thumbnail]').attr('src', dataUrl);
        });
        this.on('uploadprogress', function (file: File, progress: number, bytesSent: number) {
          let $thumbnail = $(file.previewElement);
          $thumbnail.find('[data-tu-uploadprogress]').width(progress + '%');
        });

        this.on('addedfile', self.onFileAdded);
        this.on('canceled', self.onCanceled);
        this.on('sending', self.onSending.bind(self));
        this.on('sendingmultiple', self.onSendingMultiple.bind(self));
        this.on('success', self.onUploadComplete.bind(self));

          this.optionCallbacks = self.options;

          if (self.options.onUploadComplete) {
            this.on('success', function (file: File, responseText: any, e: any) {
              this.optionCallbacks.onUploadComplete(file, responseText, e);
            });
          }

          if (self.options.onFileAdded) {
            this.on('addedfile', function (file: File) {
              this.optionCallbacks.onFileAdded(file)
            });
          }

          if (self.options.onError) {
            this.on('error', function (file: File, errorMessage: string, e?: any) {
              this.optionCallbacks.onError(file, errorMessage, e);
            });
          }
        else
          this.on('error', self.onError);

          if (self.options.onThumbnailGenerated) {
            this.on('thumbnail', function (file: File, dataUrl: string) {
              this.optionCallbacks.onThumbnailGenerated(file, dataUrl);
            });
          }
      }
    };
  }

  private getPreviewTemplate(): string {
    let $previewTemplateContainer = this.options.$previewContainer.find(ThumbnailUpload.previewTemplate.asDataSelector());
    if ($previewTemplateContainer.length > 0) {
      let $previewTemplate = $previewTemplateContainer.find(':first');
      $previewTemplate.find(ThumbnailUpload.uploadSuccess.asDataSelector() + ', ' + ThumbnailUpload.uploadError.asDataSelector()).attr('hidden', 'hidden');
      return $previewTemplateContainer.html();
    }
  }

  private getDefaultPreviewTemplate(): string {
    return '<div class="gu-1-3">' +
      '<div class="thumbnail-container">' +
      '<div class="thumbnail">' +
      '<img data-tu-thumbnail="on" hidden="hidden" />' +
      '<div class="thumbnail-overlay" ' + ThumbnailUpload.tuUploading.asData() + '="on">' +
      '<i class="thumbnail-icon icon-lg icon-spinner-cog"></i>' +
      '<br />' +
      '<span class="thumbnail-text">Uploading</span>' +
      '</div>' +
      ' <span class="tu-upload-progress" ' + ThumbnailUpload.tuUploading.asData() + ' data-tu-uploadprogress="on"></span>' +
      '<a class="thumbnail-overlay" ' + ThumbnailUpload.uploadError.asData() + '="on" data-tu-error-remove="on" hidden="hidden">' +
      '<i class="thumbnail-icon icon-lg icon-error icon-color-danger"></i>' +
      '<br />' +
      '<span>Error uploading. Please try again.</span>' +
      '</a>' +
      '</div>' +
      '<div class="thumbnail-undertext">' +
      '<p data-tu-name="on"></p>' +
      '<a ' + ThumbnailUpload.tuUploading.asData() + '="on" data-remote="on" ' + ThumbnailUpload.thumbnailRemove.asData() + '="on">(cancel)</a>' +
      '<a ' + ThumbnailUpload.uploadSuccess.asData() + '="on" data-remote="on" hidden="hidden" ' + ThumbnailUpload.thumbnailRemove.asData() + '="on">(remove)</a>' +
      '<a ' + ThumbnailUpload.uploadError.asData() + '="on" data-remote="on" hidden="hidden" ' + ThumbnailUpload.thumbnailRemove.asData() + '="on">(remove)</a>' +
      '</div>' +
      '</div>' +
      '</div>';
  }

  private onFileAdded(file: File): void {
    let $thumbnail = $(file.previewElement);
    let thumbnailId = $(ThumbnailUpload.thumbnailId.asDataSelector()).length;
    $thumbnail.attr(ThumbnailUpload.thumbnailId.asData(), thumbnailId);

    window.Application.wireupConventions($(file.previewElement));
  }

  private onCanceled(file: File): void {
    $(file.previewElement).remove();
  }

  private onSending(file: File, xhr: JQueryXHR, formData: FormData): void {
    let $thumbnail = $(file.previewElement);
    if ($thumbnail.data('tu-already-sent'))
      return;

    this.addFormData(formData, $thumbnail.data(ThumbnailUpload.thumbnailId));
  }

  private onSendingMultiple(files: File[], xhr: JQueryXHR, formData: FormData): void {
    let thumbnailIds = '';
    for (let i = 0; i < files.length; i++) {
      let $thumbnail = $(files[i].previewElement);
      if (thumbnailIds === '')
        thumbnailIds = $thumbnail.data(ThumbnailUpload.thumbnailId);
      else
        thumbnailIds = thumbnailIds + ',' + $thumbnail.data(ThumbnailUpload.thumbnailId);
      $thumbnail.data('tu-already-sent', true);
    }

    this.addFormData(formData, thumbnailIds);
  }

  private addFormData(formData: FormData, thumbnailIds: string) {
    formData.append('thumbnailIds', thumbnailIds);
    let token = new RequestVerificationToken();
    formData.append(token.key, token.value);
  }

  private onUploadComplete(file: File, responseText: string | IUploadCompleteResponse[], e: any): void {
    if (!responseText || !responseText.length) {
      this.dropzone.emit('error', file, 'An unexpected error has occured', e);
      return;
    }
    else if (responseText == ThumbnailUpload.tuEmpty) {
      return;
    }

    let response = this.getServerResponseFor(file, <IUploadCompleteResponse[]>responseText, e);
    let $thumbnail = $(file.previewElement);

    if (response.html)
      $thumbnail.replaceWith($(response.html));
    else {
      $thumbnail.find(ThumbnailUpload.tuUploading.asDataSelector()).remove();

      if (response.errorText) {
        if (this.options.onError)
          this.dropzone.emit('error', file, response.errorText, e);
        else
          this.onUploadError($thumbnail, response.errorText);
      }
      else if (response.serverId)
        this.onUploadSuccess($thumbnail, response);
      else {
        this.dropzone.emit('error', file, 'An unexpected error has occured', e);
        throw new Error('Server response not properly formatted for thumbnailId' + response.thumbnailId);
      }
    }
  }

  private getServerResponseFor(file: File, responseText: IUploadCompleteResponse[], e: any): IUploadCompleteResponse {
    let thumbnailId = $(file.previewElement).data(ThumbnailUpload.thumbnailId);
    let response;
    for (let i = 0; i < responseText.length; i++) {
      if (responseText[i].thumbnailId === thumbnailId) {
        response = responseText[i];
        break;
      }
    }

    if (!response) {
      this.dropzone.emit('error', file, 'An unexpected error has occured', e);
      throw new Error('No response from server for thumbnailId' + thumbnailId);
    }

    return response
  }

  private onUploadError($thumbnail: JQuery, errorText: string): void {
    $thumbnail.attr('title', errorText);
    $thumbnail.find(ThumbnailUpload.uploadError.asDataSelector()).removeAttr('hidden');
    $thumbnail.find('[data-tu-error-toggle-enabled]').each(this.toggleEnabled);

    $thumbnail.find('[data-tu-error-remove]').on('click.rustig', this.removeElementAndClickDropzone.bind(this));
    $thumbnail.find(ThumbnailUpload.thumbnailRemove.asDataSelector()).on('click.rustig', this.removeThumbnail);
  }

  private removeElementAndClickDropzone(event: Event): void {
    let $thumbnail = $(event.target).closest(ThumbnailUpload.thumbnailId.asDataSelector());
    $thumbnail.remove();
    this.$element.click();
  }

  private removeThumbnail(): void {
    let $thumbnail = $(this).closest(ThumbnailUpload.thumbnailId.asDataSelector());
    $thumbnail.remove();
  }

  private onUploadSuccess($thumbnail: JQuery, response: IUploadCompleteResponse): void {
    $thumbnail.attr('data-server-id', response.serverId);
    this.addServerIdToElements($thumbnail.find('[data-tu-add-server-id]'), response.serverId);
    $thumbnail.find(ThumbnailUpload.uploadSuccess.asDataSelector()).removeAttr('hidden');
    $thumbnail.find('[data-tu-thumbnail]').removeAttr('hidden');

    this.replaceDropzoneRemoveWithRemoveAction($thumbnail, response.serverId);

    $thumbnail.find('[data-tu-success-toggle-enabled]').each(this.toggleEnabled);
  }

  private addServerIdToElements($elements: JQuery, serverId: string) {
    let self = this;

    $elements.each(function () {
      let $element = $(this);

      if ($element.is('[href]'))
        self.addServerIdToUrlAttribute($element, 'href', serverId);
      else if ($element.is('form'))
        self.addServerIdToUrlAttribute($element, 'action', serverId);
      else if ($element.is('[data-url]'))
        self.addServerIdToUrlAttribute($element, 'data-url', serverId);
      else
        $element.val(serverId);
    });
  }

  private addServerIdToUrlAttribute($element: JQuery, attribute: string, serverId: string) {
    let url = $element.attr(attribute);
    url = this.addQueryParamToUrl(url, 'serverId', serverId);
    $element.attr(attribute, url);
  }

  private replaceDropzoneRemoveWithRemoveAction($thumbnail: JQuery, serverId: string): void {
    let removeUrl = this.$element.data('tu-remove-url');

    if (removeUrl) {
      removeUrl = this.addQueryParamToUrl(removeUrl, 'serverId', serverId);
      let $removeElement = $thumbnail.find(ThumbnailUpload.thumbnailRemove.asDataSelector() + ':not([hidden])');
      $removeElement.off('click.rustig');
      $removeElement.attr('href', removeUrl);
    }
  }

  private addQueryParamToUrl(url: string, key: string, value: string): string {
    let separator = url.indexOf('?') == -1 ? '?' : '&';
    return url + separator + key + '=' + value;
  }

  private toggleEnabled(): void {
    $(this).toggleEnabled($(this).isDisabled());
  }

  private onError(file: File, errorMessage: string, xhr: JQueryXHR): void {
    $(file.previewElement).remove();
    Modal.openErrorModal(errorMessage);
  }

  private updateDropzone($uploadPreserver: JQuery): void {
    this.dropzone = $uploadPreserver[0].dropzone;

    let $previewContainer = this.options.$previewContainer;
    let $previewContainerPrepend = $previewContainer.find(ThumbnailUpload.prependAttribute.asDataSelector());

    this.dropzone.updatePreviewContainer($previewContainer[0], $previewContainerPrepend[0]);
    this.dropzone.addClickableElement(this.$element[0], this.dropzone);
    this.addUploadingAndQueuedFilesToDropzone($previewContainerPrepend);

      this.dropzone.optionCallbacks = this.options;
  }

  private addUploadingAndQueuedFilesToDropzone($prepend: JQuery): void {
      let files = this.getUploadingFiles().concat(this.dropzone.getQueuedFiles());

    for (let i = 0; i < files.length; i++) {
      let $thumbnail = $(files[i].previewElement);
      if ($prepend.length > 0)
        $thumbnail.insertBefore($prepend);
      else
        $thumbnail.appendTo(this.options.$previewContainer);
    }
  }

  private static confirm(text: string, callback: any): void {
    Confirmation.show({
      content: text,
      callback: function () {
        Modal.close();
        callback();
      }
    });
  }

}

export interface IThumbnailUploadOptions {
  $dropzone: JQuery,
  onUploadComplete?: (file: File, responseText: any, e: any) => void;
  onFileAdded?: (file: File) => void;
  onError?: (file: File, errorMessage: string, e?: any) => void;
  onThumbnailGenerated?: (file: File, dataUrl: string) => void;
  $previewContainer: JQuery;
  uniqueId: number;
  accept?: (file: File, done: any) => void;
}

export interface IUploadCompleteResponse {
  thumbnailId: string,
  html?: string,
  serverId?: string,
  errorText?: string
}

function Plugin(option) {
  return this.each(function () {
    var $this = $(this);
    var data = $this.data('rustig.thumbnailupload');
    var options = typeof option == 'object' && option;

    if (!data && !$this.is('[data-tu-ignore]')) {
      data = new ThumbnailUpload({
        $dropzone: $this.find('[data-tu-url]'),
        $previewContainer: $this,
        uniqueId: parseInt($this.data('thumbnailupload-id'))
      });
      $this.data('rustig.thumbnailupload', data);
    }
    if (typeof option == 'string')
      data[option]();
  });
}

$.fn.thumbnailupload = Plugin;
